/**
 * Session du portail client.
 *
 * Le jeton JWT est conserve dans `localStorage`. C'est le compromis
 * habituel d'une application a jeton porteur, et il a un cout assume :
 * un jeton en localStorage est lisible par tout script qui s'executerait
 * dans la page. La protection reelle contre cela est l'absence
 * d'injection de script (aucun `dangerouslySetInnerHTML` ici, aucune
 * dependance chargee depuis un CDN) et la duree de vie du jeton, fixee
 * par le serveur.
 *
 * Un cookie `HttpOnly` serait plus solide, mais l'API n'en emet pas :
 * le basculer serait un chantier serveur a part, pas un choix de front.
 */
const CLE_JETON = "sign365.token";
const CLE_TENANT = "sign365.tenant";

/** Toute lecture de stockage peut echouer (navigation privee, site data
 *  bloque). On ne veut jamais qu'une page blanche en resulte. */
function lire(cle: string): string | null {
  try {
    return window.localStorage.getItem(cle);
  } catch {
    return null;
  }
}

function ecrire(cle: string, valeur: string | null): void {
  try {
    if (valeur === null) window.localStorage.removeItem(cle);
    else window.localStorage.setItem(cle, valeur);
  } catch {
    // Stockage indisponible : la session ne survivra pas au rechargement,
    // mais l'application reste utilisable pendant la visite.
  }
}

export const session = {
  token: () => lire(CLE_JETON),
  /** Le slug est memorise pour le pre-remplir a la prochaine connexion :
   *  c'est la donnee que les utilisateurs oublient le plus souvent. */
  tenantSlug: () => lire(CLE_TENANT),
  isAuthenticated: () => lire(CLE_JETON) !== null,

  /** Memorise l'identifiant SANS ouvrir de session : juste apres une
   *  invitation acceptee, l'invite n'est pas encore connecte, mais il ne
   *  connait pas cet identifiant - l'e-mail ne donnait que le nom. */
  memoriserOrganisation(tenantSlug: string) {
    ecrire(CLE_TENANT, tenantSlug);
  },

  ouvrir(token: string, tenantSlug: string) {
    ecrire(CLE_JETON, token);
    ecrire(CLE_TENANT, tenantSlug);
  },

  fermer() {
    ecrire(CLE_JETON, null);
    // Le slug est conserve : ce n'est pas un secret, et le retaper a
    // chaque connexion est une friction inutile.
  },
};
